import { createContext, useCallback, useContext, useEffect, useMemo, useState, type FC, type ReactNode } from 'react';
import { GlobalTheme } from '@carbon/react';

import { supabase } from '@maestra/core/lib/supabase';


// ─── O TEMA DA WEB: `white` (claro) OU `g100` (escuro), OS DO CARBON ──────────────────────
//
// A preferência é da pessoa, e mora NA CONTA (`user_metadata.tema` do Supabase): quem escolhe o
// escuro no computador o encontra no celular. O navegador guarda uma cópia (`localStorage`) só
// para a primeira pintura não piscar antes de a sessão carregar. Sem escolha nenhuma, vale a do
// sistema (`prefers-color-scheme`).
//
// O tema resolvido vira a
// classe `cds--white`/`cds--g100` no <html>, que é o que o carbon.scss usa para trocar TODOS os
// `--cds-*` de uma vez.
//

export type PreferenciaDeTema = 'sistema' | 'claro' | 'escuro';

/** Os dois temas do Carbon que a web usa. */
export type TemaCarbon = 'white' | 'g100';

const CHAVE = 'maestra:tema';
const CLASSES: Record<TemaCarbon, string> = { white: 'cds--white', g100: 'cds--g100' };

const ehPreferencia = (v: unknown): v is PreferenciaDeTema => v === 'sistema' || v === 'claro' || v === 'escuro';

const lerPreferencia = (): PreferenciaDeTema => {
  try {
    const salvo = window.localStorage.getItem(CHAVE);
    if (ehPreferencia(salvo)) return salvo;
  } catch {
    // Sem armazenamento (janela privada): fica o padrão.
  }
  return 'sistema';
};

const guardar = (p: PreferenciaDeTema) => {
  try {
    window.localStorage.setItem(CHAVE, p);
  } catch {
    // Sem armazenamento: vale a da conta.
  }
};

const sistemaEscuro = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches;

const resolver = (p: PreferenciaDeTema, escuroNoSistema: boolean): TemaCarbon =>
  p === 'escuro' || (p === 'sistema' && escuroNoSistema) ? 'g100' : 'white';

interface ContextoDoTema {
  preferencia: PreferenciaDeTema;
  tema: TemaCarbon;
  definirPreferencia: (p: PreferenciaDeTema) => void;
}

const Contexto = createContext<ContextoDoTema>({
  preferencia: 'sistema',
  tema: 'white',
  definirPreferencia: () => {},
});

export const useTema = () => useContext(Contexto);

export const TemaProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [preferencia, setPreferencia] = useState<PreferenciaDeTema>(lerPreferencia);
  const [escuroNoSistema, setEscuroNoSistema] = useState(sistemaEscuro);

  useEffect(() => {
    const consulta = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!consulta) return undefined;
    const mudou = (e: MediaQueryListEvent) => setEscuroNoSistema(e.matches);
    consulta.addEventListener('change', mudou);
    return () => consulta.removeEventListener('change', mudou);
  }, []);

  // A escolha guardada na conta vence a cópia do navegador assim que a sessão chega (e a cada
  // troca de sessão: entrar noutra conta traz o tema dela).
  useEffect(() => {
    const daConta = (tema?: unknown) => {
      if (!ehPreferencia(tema)) return;
      setPreferencia(tema);
      guardar(tema);
    };
    void supabase.auth.getSession().then(({ data }) => daConta(data.session?.user?.user_metadata?.tema));
    const { data } = supabase.auth.onAuthStateChange((_evento, sessao) => daConta(sessao?.user?.user_metadata?.tema));
    return () => data.subscription.unsubscribe();
  }, []);

  const tema = resolver(preferencia, escuroNoSistema);

  useEffect(() => {
    const html = document.documentElement;
    Object.values(CLASSES).forEach((c) => html.classList.remove(c));
    html.classList.add(CLASSES[tema]);
    html.style.colorScheme = tema === 'g100' ? 'dark' : 'light';
  }, [tema]);

  const definirPreferencia = useCallback((p: PreferenciaDeTema) => {
    setPreferencia(p);
    guardar(p);
    // Na conta, para valer nos outros dispositivos. Sem sessão (ou sem rede) fica só a do navegador.
    void supabase.auth.getSession().then(({ data }) => {
      if (data.session) return supabase.auth.updateUser({ data: { tema: p } });
      return undefined;
    }).catch(() => undefined);
  }, []);

  const valor = useMemo(() => ({ preferencia, tema, definirPreferencia }), [preferencia, tema, definirPreferencia]);

  return (
    <Contexto.Provider value={valor}>
      <GlobalTheme theme={tema}>{children}</GlobalTheme>
    </Contexto.Provider>
  );
};
